import { FeatureTableOptions } from 'types'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { GeckoWeld } from 'types/assetModel/features/welds/geckoWelds'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

export interface GeckoHorizontalWeldJSON {
  name: string | undefined
  type: GeckoFeatureModelType.HorizontalWeld
  feature_geometry: {
    x_start: number
    x_end: number
    y: number
  }
}

export const NewGeckoHorizontalWeldJSON: GeckoHorizontalWeldJSON = {
  name: 'New Horizontal Weld',
  type: GeckoFeatureModelType.HorizontalWeld,
  feature_geometry: {
    x_start: 0,
    x_end: 0,
    y: 0,
  },
}

export class GeckoHorizontalWeld extends GeckoWeld {
  public readonly type = GeckoFeatureModelType.HorizontalWeld

  private constructor(
    componentID: string,
    weldID: string,
    weldJson: GeckoHorizontalWeldJSON
  ) {
    const name = parseString(weldJson.name)
    const feature_geometry = weldJson.feature_geometry as Record<string, number>
    super(
      componentID,
      weldID,
      name,
      feature_geometry.x_start as number,
      feature_geometry.y as number,
      feature_geometry.x_end as number,
      feature_geometry.y as number
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJson: Record<string, GeckoHorizontalWeldJSON>
  ): Map<string, GeckoHorizontalWeld> {
    return Object.keys(recordsJson).reduce((resultObj, weldID) => {
      resultObj.set(
        weldID,
        new GeckoHorizontalWeld(componentID, weldID, recordsJson[weldID])
      )
      return resultObj
    }, new Map<string, GeckoHorizontalWeld>())
  }

  public static CreateFromJSON(
    componentID: string,
    weldID: string,
    weldJson: GeckoHorizontalWeldJSON
  ): GeckoHorizontalWeld {
    return new GeckoHorizontalWeld(componentID, weldID, weldJson)
  }

  public asJSON() {
    return generateHorizontalWeld(
      this.name,
      this.feature_geometry.y.min,
      this.feature_geometry.x.min,
      this.feature_geometry.x.max
    )
  }
}

export function generateHorizontalWeld(
  name: string | undefined,
  y: GeckoMeasurement,
  xStart: GeckoMeasurement,
  xEnd: GeckoMeasurement
): GeckoHorizontalWeldJSON {
  const horizontalWeldJSON: GeckoHorizontalWeldJSON = {
    name: name ?? undefined,
    type: GeckoFeatureModelType.HorizontalWeld,
    feature_geometry: {
      y: y
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      x_start: xStart
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      x_end: xEnd
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
    },
  }

  return horizontalWeldJSON
}

export const HorizontalWeldModelFormFields = (
  weldJSON: GeckoHorizontalWeldJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Weld Name',
      value: weldJSON.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: weldJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_start',
      label: 'X Start',
      value: weldJSON.feature_geometry.x_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x_end',
      label: 'X End',
      value: weldJSON.feature_geometry.x_end,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const HorizontalWeldJSONFromFormFields = (formFields: FormField[]) => {
  return generateHorizontalWeld(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x_start')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x_end')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    )
  )
}

export const HorizontalWeldFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.HorizontalWeld,
  title: 'Horizontal Welds',
  singularName: 'Horizontal Weld',
  fields: ['y', 'x_start', 'x_end'],
  sortBy: [
    { fieldName: 'x_start', order: 'asc' },
    { fieldName: 'y', order: 'asc' },
  ],
  disableEditing: true,
  tableMessage:
    'Adding / Editing of Horizontal Welds is disabled as they are automatically generated from Plates and/or Courses',
}
